import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { getMockRubiconResponse, MOCK_VALID_CREDENTIALS } from '@/mocks/import-external-connections.mock';
import { useUserStore } from '@/store';
import conf from '@/utils/conf';
import { createLogger, LOG_NAMESPACES } from '@/utils/logger';

const apiLogger = createLogger(LOG_NAMESPACES.API);

const API_PATH_PREFIX = '/api/v1';
const API_ENDPOINT_BASE = conf.apiHost + API_PATH_PREFIX;

// Guard to prevent multiple simultaneous 401 logout redirects
let isLoggingOut = false;

const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_ENDPOINT_BASE,
  timeout: 155000,
  withCredentials: true,
});

// ── DEV mock interceptor — bypasses network for rubicon/* routes ────────────
if (import.meta.env.DEV) {
  axiosInstance.interceptors.request.use((config) => {
    const url = config.url ?? '';
    if (!url.includes('rubicon/')) {
      return config;
    }

    const raw = config.data;
    // Skip mock interception for FormData (file uploads) — cannot be parsed as JSON
    if (raw instanceof FormData) {
      return config;
    }
    const body: Record<string, unknown> =
      raw == null ? {} : typeof raw === 'string' ? (JSON.parse(raw) as Record<string, unknown>) : (raw as Record<string, unknown>);
    // Only intercept requests whose host matches a known mock host.
    // Real remote connections pass through to the actual backend.
    const mockHosts = Object.values(MOCK_VALID_CREDENTIALS).map((c) => c.host);
    const isMockHost = mockHosts.includes(body.host as string);
    if (!isMockHost) {
      return config;
    }

    const mock = getMockRubiconResponse(url, body);
    if (!mock) {
      return config;
    }

    // Replace the transport adapter so Axios never opens a real socket.
    // The adapter must return a settled promise that Axios can unwrap.
    config.adapter = () => {
      const response = {
        data: mock.data,
        status: mock.status,
        statusText: mock.status < 400 ? 'OK' : 'Error',
        headers: {},
        config,
        request: {},
      };

      if (mock.status >= 200 && mock.status < 300) {
        return Promise.resolve(response);
      }

      return Promise.reject(new axios.AxiosError((mock.data as any)?.message ?? 'Mock error', String(mock.status), config, {}, response));
    };

    return config;
  });
}

// Request interceptor — Content-Type handling for JSON / FormData
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const isFormDataRequest = config.data instanceof FormData;

    if (config.headers) {
      if (isFormDataRequest) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      } else if (!config.headers['Content-Type'] && !config.headers['content-type']) {
        config.headers['Content-Type'] = 'application/json';
      }
    }

    // Log API requests in development
    apiLogger.debug(
      `🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`,
      {
        headers: config.headers,
        data: config.data,
        params: config.params,
      }
    );
    return config;
  },
  (error) => {
    apiLogger.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const { isAuthenticated } = useUserStore.getState();
      // The user-service endpoints (e.g. /user/profile) are not yet migrated to cookie auth
      // and still reject the session cookie. Skip logout for those so we don't redirect the
      // user away from the page while the backend migration is in progress.
      // TODO(backend): Remove this exception once /api/v1/user/* accepts session cookies.
      // Issue: Backend user-service endpoints require cookie-auth support.
      const url = error.config?.url ?? '';
      if (url.includes('/user/') || url.includes('/user/profile')) {
        apiLogger.warn('User endpoint rejected session cookie – skipping logout', {
          url,
          method: error.config?.method,
        });
        return Promise.reject(error);
      }
      // Only logout if the user is currently authenticated.
      // This avoids clearing state during login attempts where 401
      // means invalid credentials, not an expired session.
      if (isAuthenticated && !isLoggingOut) {
        isLoggingOut = true;
        apiLogger.warn('Unauthorized – session expired, logging out', {
          url: error.config?.url,
          method: error.config?.method,
        });
        useUserStore.getState().logout();
        // Reset the flag after a short delay to allow pending requests
        // to resolve without each one triggering a separate logout.
        setTimeout(() => {
          isLoggingOut = false;
        }, 1000);
      }
    } else if (error.response?.status === 403) {
      apiLogger.warn('Access forbidden', {
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
      });
    } else if (error.response?.status === 500) {
      apiLogger.error('Server error occurred', {
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
      });
      apiLogger.error('API request failed', {
        status: error.response?.status,
        message: error.response?.data || error.message,
        url: error.config?.url,
        method: error.config?.method,
      });
    }
    return Promise.reject(error);
  }
);

/**
 * Extract a human-readable error message from an Axios or generic error.
 * Centralises the various backend error shapes into a single helper.
 */
export function getErrorMessage(error: Error | unknown): string {
  if (isAxiosError(error)) {
    const axiosErr = error as AxiosError<{ message?: string; error?: { Data?: string; Description?: string } }>;
    return (
      axiosErr.response?.data?.error?.Description ||
      axiosErr.response?.data?.error?.Data ||
      axiosErr.response?.data?.message ||
      axiosErr.response?.statusText ||
      axiosErr.message ||
      'An unknown Axios error occurred'
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
}

export default axiosInstance;
export const { isAxiosError } = axios;
